import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.addEventListener('pointerover', this.#handleHoverZoomEnter, { signal });
    this.addEventListener('pointermove', this.#handleHoverZoomMove, { signal });
    this.addEventListener('pointerout', this.#handleHoverZoomLeave, { signal });
    window.addEventListener('scroll', this.#handleHoverZoomViewportChange, { signal, passive: true });
    window.addEventListener('resize', this.#handleHoverZoomViewportChange, { signal, passive: true });
  }

  #controller = new AbortController();

  /** @type {HTMLElement | null} */
  #hoverZoomMedia = null;

  /** @type {HTMLImageElement | null} */
  #hoverZoomImage = null;

  /** @type {HTMLDivElement | null} */
  #hoverZoomPane = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#hoverZoomPane?.remove();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * @param {PointerEvent} event
   */
  #handleHoverZoomEnter = (event) => {
    if (!this.#canHoverZoom(event)) return;

    const media = this.#getHoverZoomMedia(event.target);
    if (!media || (event.relatedTarget instanceof Node && media.contains(event.relatedTarget))) return;

    this.#resetHoverZoomMedia();
    this.#hoverZoomMedia = media;
    this.#hoverZoomImage = media.querySelector('img.product-media__image');
    media.classList.add('is-hover-zooming');

    const pane = this.#getHoverZoomPane();
    const image = this.#hoverZoomImage;
    if (!(image instanceof HTMLImageElement) || !pane) return;

    pane.style.backgroundImage = `url(${JSON.stringify(image.currentSrc || image.src)})`;
    pane.classList.add('is-visible');
    this.#updateHoverZoomOrigin(event, media);
    this.#loadHoverZoomImage(image);
  };

  /**
   * @param {PointerEvent} event
   */
  #handleHoverZoomMove = (event) => {
    if (!this.#hoverZoomMedia || !this.#canHoverZoom(event)) return;
    this.#updateHoverZoomOrigin(event, this.#hoverZoomMedia);
  };

  /**
   * @param {PointerEvent} event
   */
  #handleHoverZoomLeave = (event) => {
    const media = this.#hoverZoomMedia;
    if (!media || (event.relatedTarget instanceof Node && media.contains(event.relatedTarget))) return;

    this.#resetHoverZoomMedia();
  };

  #handleHoverZoomViewportChange = () => {
    if (!this.#hoverZoomMedia) return;
    this.#syncHoverZoomPane(this.#hoverZoomMedia);
  };

  /**
   * @param {PointerEvent} event
   * @returns {boolean}
   */
  #canHoverZoom(event) {
    if (event.pointerType !== 'mouse') return false;
    if (!this.closest('#MainContent[data-template="product"] .product-information')) return false;

    return window.matchMedia('(min-width: 1200px) and (hover: hover) and (pointer: fine)').matches;
  }

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #getHoverZoomMedia(target) {
    if (!(target instanceof Element)) return null;

    const media = target.closest('slideshow-slide.product-media-container--image');
    if (!(media instanceof HTMLElement) || !this.contains(media)) return null;
    if (media.getAttribute('aria-hidden') === 'true') return null;

    return media;
  }

  /**
   * @param {PointerEvent} event
   * @param {HTMLElement} media
   */
  #updateHoverZoomOrigin(event, media) {
    const rect = media.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));

    const pane = this.#syncHoverZoomPane(media);
    if (!pane) return;

    const zoomScale = Math.max(4.5, pane.offsetWidth / rect.width + 0.35, pane.offsetHeight / rect.height + 0.35);
    const lensWidth = Math.min(rect.width, pane.offsetWidth / zoomScale);
    const lensHeight = Math.min(rect.height, pane.offsetHeight / zoomScale);
    const pointerX = event.clientX - rect.left;
    const pointerY = event.clientY - rect.top;
    const lensLeft = Math.min(rect.width - lensWidth, Math.max(0, pointerX - lensWidth / 2));
    const lensTop = Math.min(rect.height - lensHeight, Math.max(0, pointerY - lensHeight / 2));

    media.style.setProperty('--artista-hover-zoom-x', `${x}%`);
    media.style.setProperty('--artista-hover-zoom-y', `${y}%`);
    media.style.setProperty('--artista-hover-lens-width', `${lensWidth}px`);
    media.style.setProperty('--artista-hover-lens-height', `${lensHeight}px`);
    media.style.setProperty('--artista-hover-lens-left', `${lensLeft}px`);
    media.style.setProperty('--artista-hover-lens-top', `${lensTop}px`);

    pane.style.backgroundPosition = `${x}% ${y}%`;
    pane.style.backgroundSize = `${rect.width * zoomScale}px ${rect.height * zoomScale}px`;
  }

  /**
   * @returns {HTMLDivElement | null}
   */
  #getHoverZoomPane() {
    if (this.#hoverZoomPane?.isConnected) return this.#hoverZoomPane;

    const pane = document.createElement('div');
    pane.className = 'artista-product-hover-zoom';
    pane.setAttribute('aria-hidden', 'true');
    document.body.append(pane);
    this.#hoverZoomPane = pane;

    return pane;
  }

  /**
   * @param {HTMLElement} media
   * @returns {HTMLDivElement | null}
   */
  #syncHoverZoomPane(media) {
    const pane = this.#hoverZoomPane;
    const productInformation = this.closest('.product-information');
    const content = productInformation?.querySelector('.product-details > .group-block > .group-block-content');
    const purchasePanel = content?.querySelector(':scope > .artista-purchase-panel');
    if (!pane || !(content instanceof HTMLElement) || !(purchasePanel instanceof HTMLElement)) return null;

    const mediaRect = media.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    const purchaseRect = purchasePanel.getBoundingClientRect();
    const gap = Math.max(16, purchaseRect.left - (contentRect.left + (contentRect.width - purchaseRect.width)));
    const paneWidth = purchaseRect.left - contentRect.left - gap;
    const availableHeight = window.innerHeight - mediaRect.top - 16;

    if (paneWidth < 260 || availableHeight < 220) {
      this.#resetHoverZoomMedia();
      return null;
    }

    pane.style.left = `${contentRect.left}px`;
    pane.style.top = `${mediaRect.top}px`;
    pane.style.width = `${paneWidth}px`;
    pane.style.height = `${Math.min(mediaRect.height, availableHeight)}px`;

    return pane;
  }

  #resetHoverZoomMedia() {
    const media = this.#hoverZoomMedia;
    media?.classList.remove('is-hover-zooming');
    media?.style.removeProperty('--artista-hover-zoom-x');
    media?.style.removeProperty('--artista-hover-zoom-y');
    media?.style.removeProperty('--artista-hover-lens-width');
    media?.style.removeProperty('--artista-hover-lens-height');
    media?.style.removeProperty('--artista-hover-lens-left');
    media?.style.removeProperty('--artista-hover-lens-top');
    this.#hoverZoomPane?.classList.remove('is-visible');
    this.#hoverZoomMedia = null;
    this.#hoverZoomImage = null;
  }

  /**
   * @param {HTMLImageElement} image
   */
  #loadHoverZoomImage(image) {
    const source = image.dataset.maxResolution || image.getAttribute('data_max_resolution');
    const pane = this.#hoverZoomPane;
    if (!source || !pane) return;

    if (image.dataset.hoverZoomStatus === 'ready') {
      pane.style.backgroundImage = `url(${JSON.stringify(source)})`;
      return;
    }

    if (image.dataset.hoverZoomStatus === 'loading') return;

    image.dataset.hoverZoomStatus = 'loading';
    const highResolutionImage = new Image();
    highResolutionImage.decoding = 'async';
    highResolutionImage.src = source;

    highResolutionImage.addEventListener(
      'load',
      () => {
        if (!image.isConnected) return;

        image.dataset.hoverZoomStatus = 'ready';
        if (this.#hoverZoomImage === image && this.#hoverZoomPane) {
          this.#hoverZoomPane.style.backgroundImage = `url(${JSON.stringify(source)})`;
        }
      },
      { once: true }
    );

    highResolutionImage.addEventListener(
      'error',
      () => {
        delete image.dataset.hoverZoomStatus;
      },
      { once: true }
    );
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
